import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { RelationalOperator } from '@mtna/data-core-ts';

import { RdsChip } from '../chips/index';
import {
  RDS_TEXT_PARAMETER_OPTIONS,
  RdsTextFilter,
  RdsTextOperations,
  RdsTextParameter,
  RdsTextParameterOption,
} from './text-filter.model';

const DEFAULT_SUPPORTED_TEXT_FILTER_OPERATIONS: RdsTextOperations[] = ['EQ', 'NE'];

@Component({
  selector: 'rds-c-text-filter',
  templateUrl: './text-filter.component.html',
  styleUrls: ['./text-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsTextFilterComponent implements OnChanges {
  static ngAcceptInputType_supportedOperators: RelationalOperator[] | null | undefined;

  /** Text Filters */
  @Input() filters: Array<RdsTextFilter> | null | undefined = [];

  /** Whether to use the large chip style variant */
  @HostBinding('class.large-chips')
  @Input()
  largeChips = false;

  /** The supported `RelationalOperator`s. i.e. what operaters does the current data product support. Defaults to EQ and NE. */
  @Input()
  set supportedOperators(operators: RelationalOperator[]) {
    this._supportedOperators = operators || DEFAULT_SUPPORTED_TEXT_FILTER_OPERATIONS;
    this.determineSelectOptions();
    this.cdr.markForCheck();
  }
  get supportedOperators(): RelationalOperator[] {
    return this._supportedOperators;
  }
  private _supportedOperators: RelationalOperator[] = DEFAULT_SUPPORTED_TEXT_FILTER_OPERATIONS;

  /** Event emitted when the user adds new filter */
  @Output() add = new EventEmitter<RdsTextFilter>();
  /** Event emitted when the user removes a filter */
  @Output() remove = new EventEmitter<RdsTextFilter>();

  inputControl = new FormControl('', Validators.minLength(1));

  /** A record to indicate which options should be enabled based on the current selected filters */
  enabledOptions: Record<RdsTextParameter, boolean> = {
    MATCHES: true,
    NOT_MATCHES: true,
    CONTAINS: true,
    NOT_CONTAINS: true,
    STARTS_WITH: true,
    NOT_STARTS_WITH: true,
    NOT_ENDS_WITH: true,
    ENDS_WITH: true,
    EQ: true,
    NE: true,
  };

  /** Select options to choose what type of filter operator to use. */
  filterOptions: RdsTextParameterOption[] = [];

  /** Added filter operation and value from inputs */
  newValue = new RdsTextFilter(null, null);

  constructor(private cdr: ChangeDetectorRef) {
    // Set initial, default options
    this.determineSelectOptions();
  }

  /** Given the current supported operations, what select options should be visible. */
  determineSelectOptions() {
    let newOptions: RdsTextParameterOption[] = [];
    this.supportedOperators.forEach((operator) => {
      switch (operator) {
        // We break down the LIKE operator to three more helpful options: starts with, contains, and ends with.
        // All of which actually use LIKE in the API call
        case 'LIKE': {
          const options: RdsTextParameterOption[] = RDS_TEXT_PARAMETER_OPTIONS.filter(
            (o) => o.type === 'CONTAINS' || o.type === 'STARTS_WITH' || o.type === 'ENDS_WITH'
          );
          newOptions = [...newOptions, ...options];
          break;
        }
        case 'NOT_LIKE': {
          const options: RdsTextParameterOption[] = RDS_TEXT_PARAMETER_OPTIONS.filter(
            (o) => o.type === 'NOT_CONTAINS' || o.type === 'NOT_STARTS_WITH' || o.type === 'NOT_ENDS_WITH'
          );
          newOptions = [...newOptions, ...options];
          break;
        }
        default: {
          const option: RdsTextParameterOption | undefined = RDS_TEXT_PARAMETER_OPTIONS.find((o) => o.type === operator);
          if (option) {
            newOptions = [...newOptions, option];
          }
        }
      }
    });
    this.filterOptions = newOptions;
  }

  /**
   * Emit added values and clear the inputs
   */
  handleAdd() {
    const text = (this.inputControl.value as string).trim();
    if (!this.newValue.parameter || text.length) {
      this.newValue.text = text;
      this.add.emit(this.newValue);
      this.newValue = new RdsTextFilter(null, null);
      this.inputControl.reset(null);
    } else {
      this.inputControl.setValue(null);
      this.inputControl.setErrors({ minlength: true });
    }
  }

  handleRemove(chip: RdsChip) {
    if (this.filters) {
      const found = this.filters.find((f) => (!f.parameter ? false : f.parameter.type + f.text === chip.id));
      this.remove.emit(found);
    }
  }

  /**
   * Any time the filters change, determine which operations are still applicable, if any
   */
  ngOnChanges(changes: SimpleChanges) {
    const filtersChange: SimpleChange = changes.filters;
    if (filtersChange) {
      const changedFilters: Array<RdsTextFilter> | null | undefined = filtersChange.currentValue;
      if (changedFilters) {
        // start all as true and flip to false
        this.enabledOptions = {
          MATCHES: true,
          NOT_MATCHES: true,
          CONTAINS: true,
          NOT_CONTAINS: true,
          STARTS_WITH: true,
          NOT_STARTS_WITH: true,
          NOT_ENDS_WITH: true,
          ENDS_WITH: true,
          EQ: true,
          NE: true,
        };
        for (const f of changedFilters) {
          switch (f.parameter?.type) {
            case 'MATCHES':
            case 'NOT_MATCHES':
            case 'CONTAINS':
            case 'NOT_CONTAINS':
            case 'STARTS_WITH':
            case 'NOT_STARTS_WITH':
            case 'ENDS_WITH':
            case 'NOT_ENDS_WITH':
            case 'NE':
              this.enabledOptions.EQ = false;
              break;
            case 'EQ':
              this.enabledOptions.EQ = false;
              this.enabledOptions.NE = false;
              this.enabledOptions.MATCHES = false;
              this.enabledOptions.NOT_MATCHES = false;
              this.enabledOptions.CONTAINS = false;
              this.enabledOptions.NOT_CONTAINS = false;
              this.enabledOptions.STARTS_WITH = false;
              this.enabledOptions.NOT_STARTS_WITH = false;
              this.enabledOptions.ENDS_WITH = false;
              this.enabledOptions.NOT_ENDS_WITH = false;
              break;
          }
        }
        this.cdr.markForCheck();
      }
    }
  }
}
